import { Menu } from "@headlessui/react";
import { Bars3Icon } from "@heroicons/react/24/outline";
import NoSsr from "@mui/material/NoSsr";
import React, { ComponentProps } from "react";
import { twMerge } from "tailwind-merge";

import { HTMLMarkdown } from "@/components/markdown/HTMLMarkdown";
import { useAuth } from "@/features/auth/shared/hooks/use-auth";
import { QuestionItems } from "@/features/winwingpt/historical-dialog-sidebar/components/QuestionItems";
import { useWgptQuota } from "@/features/winwingpt/historical-dialog-sidebar/services/get-wgpt-quota";
import { useBottomPanel } from "@/hooks/use-bottom-panel";
import { useDialog } from "@/hooks/use-dialog";
import { useLang } from "@/hooks/use-lang";

import { Button } from "ui/core/button";

export const MobileMenu = () => {
  const { t } = useLang();
  const { data } = useWgptQuota();
  const { Dialog, openDialog, closeDialog } = useDialog({
    isMobileFullScreen: false,
  });
  const { Panel, openPanel } = useBottomPanel();
  const { profile } = useAuth();
  const isPeriodicRenewalPlan =
    profile?.plan.includes("Basic") || profile?.plan.includes("Premium");

  const usageDescription = isPeriodicRenewalPlan
    ? t.winwingpt.quota
    : t.winwingpt.freeQuota;

  const InvestStyleDialog = () => (
    <Dialog title={t.winwingpt.investStyleDialog.title}>
      <div className="flex flex-col gap-5 p-6">
        <HTMLMarkdown>{t.winwingpt.investStyleDialog.content}</HTMLMarkdown>
        <Button className="mx-auto w-full max-w-sm" onClick={closeDialog}>
          {t.winwingpt.investStyleDialog.close}
        </Button>
      </div>
    </Dialog>
  );

  const BottomPanel = () => (
    <Panel
      title={
        <div className="flex flex-wrap items-baseline gap-2 text-zinc-500">
          <h3 className="text-lg">{t.winwingpt.inquiryRecord}</h3>
          {data && (
            <p>
              {usageDescription({
                counts: data.available - data.remains,
                available: data.available,
              })}
            </p>
          )}
        </div>
      }
    >
      <div className="space-y-5">
        <QuestionItems />
      </div>
    </Panel>
  );

  return (
    <div className="relative flex items-center @3xl:hidden">
      <Menu>
        <Menu.Button
          className="relative"
          aria-label="open winwin-gpt mobile menu"
        >
          <Bars3Icon width={18} height={18} className="text-zinc-700" />
        </Menu.Button>
        <Menu.Items className="absolute bottom-8 right-0 flex flex-col rounded-lg border bg-white py-2">
          <Menu.Item>
            <MenuItemWrapper onClick={openPanel}>
              {t.winwingpt.inquiryRecord}
            </MenuItemWrapper>
          </Menu.Item>
          <Menu.Item>
            <MenuItemWrapper onClick={openDialog}>
              {t.winwingpt.investStyleDialog.title}
            </MenuItemWrapper>
          </Menu.Item>
        </Menu.Items>
      </Menu>
      <InvestStyleDialog />
      <NoSsr>
        <BottomPanel />
      </NoSsr>
    </div>
  );
};

const MenuItemWrapper = React.forwardRef<HTMLDivElement, ComponentProps<"div">>(
  (props, ref) => (
    <div
      {...props}
      ref={ref}
      className={twMerge(
        "cursor-pointer whitespace-nowrap px-4 py-2 text-sm text-zinc-500 hover:bg-sky-700/5",
        props.className
      )}
    >
      {props.children}
    </div>
  )
);

MenuItemWrapper.displayName = "MenuItemWrapper";
